import React, { useEffect, useState } from "react";
import { CalendarRange, Sparkles, MapPin, Plus, Trash2, AlertTriangle } from "lucide-react";
import { useDraft } from "../../lib/draft-store";
import { getPricingCalendar } from "../../lib/api";

const DATE_MODES = [
  { key: "exact", label: "Exact date", icon: CalendarRange },
  { key: "range", label: "Date range", icon: CalendarRange },
  { key: "flexible", label: "Flexible month", icon: Sparkles },
  { key: "exploring", label: "Just exploring", icon: Sparkles },
];

const LOCATION_MODES = [
  { key: "single", label: "Single city" },
  { key: "multiple", label: "Multiple locations" },
  { key: "destination", label: "Destination — we'll travel" },
];

const CITIES = [
  "Mumbai",
  "Delhi",
  "Bengaluru",
  "Chennai",
  "Hyderabad",
  "Pune",
  "Kolkata",
  "Ahmedabad",
  "Jaipur",
  "Goa",
  "Udaipur",
  "Jodhpur",
  "Lucknow",
  "Chandigarh",
  "Other",
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function Step2DateLocation() {
  const { payload, setField, mergePayload } = useDraft();
  const date = payload?.date || {};
  const locationMode = payload?.location_mode || "";
  const locations = payload?.locations || [];

  const [calendar, setCalendar] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPricingCalendar({});
        if (!cancelled) setCalendar(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setCalendar([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const annotateDate = (iso) => {
    if (!iso) return null;
    const hit = calendar.find((c) => c.date === iso);
    return hit ? hit.status : null;
  };

  const setDateMode = (mode) => {
    mergePayload({
      date: { mode },
      peak_flag: false,
    });
  };

  const setExact = (val) => {
    const status = annotateDate(val);
    mergePayload({
      date: { mode: "exact", exact: val, status },
      peak_flag: status === "peak",
    });
  };

  const setRange = (k, v) => {
    const next = { ...(date || {}), mode: "range", [k]: v };
    const startStatus = annotateDate(next.range_start);
    const endStatus = annotateDate(next.range_end);
    const peak = startStatus === "peak" || endStatus === "peak";
    mergePayload({
      date: { ...next, statuses: { start: startStatus, end: endStatus } },
      peak_flag: peak,
    });
  };

  const setFlexible = (k, v) => {
    mergePayload({
      date: { ...(date || {}), mode: "flexible", [k]: v },
      peak_flag: false,
    });
  };

  const addLocation = () => {
    if (locations.length >= 5) return;
    setField("locations", [
      ...locations,
      { city: "", country: "India", venue_name: "", address: "", indoor_outdoor: "" },
    ]);
  };
  const updateLocation = (idx, key, value) => {
    const next = locations.map((l, i) => (i === idx ? { ...l, [key]: value } : l));
    setField("locations", next);
  };
  const removeLocation = (idx) => {
    setField(
      "locations",
      locations.filter((_, i) => i !== idx),
    );
  };

  const setLocationMode = (mode) => {
    const next = { location_mode: mode };
    if (mode !== "destination") next.destination = "";
    if (mode === "single" && locations.length === 0) {
      next.locations = [
        { city: "", country: "India", venue_name: "", address: "", indoor_outdoor: "" },
      ];
    }
    if (mode === "destination") next.locations = [];
    mergePayload(next);
  };

  return (
    <div data-testid="plan-step-2" className="space-y-12">
      <section>
        <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">When?</h2>
        <p className="mt-2 text-sm text-[#1A1A1A]/65 sm:text-base">
          We tailor crew availability and pricing to your timeline.
        </p>
        <div
          data-testid="date-mode-group"
          role="radiogroup"
          aria-label="Date selection mode"
          className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4"
        >
          {DATE_MODES.map((m) => {
            const Icon = m.icon;
            const active = date.mode === m.key;
            return (
              <button
                key={m.key}
                role="radio"
                aria-checked={active}
                data-testid={`date-mode-${m.key}`}
                type="button"
                onClick={() => setDateMode(m.key)}
                className={[
                  "flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition",
                  active
                    ? "border-[#C8A24B] bg-white"
                    : "border-[#EAE3D2] bg-white/60 hover:border-[#C8A24B]",
                ].join(" ")}
              >
                <Icon className="h-4 w-4 text-[#C8A24B]" />
                <span className="text-xs font-medium text-[#1A1A1A]">
                  {m.label}
                </span>
              </button>
            );
          })}
        </div>

        {date.mode === "exact" ? (
          <div className="mt-5">
            <label htmlFor="exact-date" className="text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
              Pick a date
            </label>
            <input
              id="exact-date"
              type="date"
              data-testid="exact-date-input"
              value={date.exact || ""}
              onChange={(e) => setExact(e.target.value)}
              className="mt-2 w-full max-w-xs rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
            />
            <DateStatus status={annotateDate(date.exact)} />
          </div>
        ) : null}

        {date.mode === "range" ? (
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="range-start" className="text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
                Earliest
              </label>
              <input
                id="range-start"
                type="date"
                data-testid="range-start-input"
                value={date.range_start || ""}
                onChange={(e) => setRange("range_start", e.target.value)}
                className="mt-2 w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
              />
              <DateStatus status={annotateDate(date.range_start)} />
            </div>
            <div>
              <label htmlFor="range-end" className="text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
                Latest
              </label>
              <input
                id="range-end"
                type="date"
                data-testid="range-end-input"
                value={date.range_end || ""}
                onChange={(e) => setRange("range_end", e.target.value)}
                className="mt-2 w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
              />
              <DateStatus status={annotateDate(date.range_end)} />
            </div>
          </div>
        ) : null}

        {date.mode === "flexible" ? (
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="flex-month" className="text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
                Month
              </label>
              <select
                id="flex-month"
                data-testid="flex-month-input"
                value={date.month || ""}
                onChange={(e) => setFlexible("month", e.target.value)}
                className="mt-2 w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
              >
                <option value="">— Select —</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="flex-year" className="text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
                Year
              </label>
              <input
                id="flex-year"
                type="number"
                min="2026"
                max="2030"
                data-testid="flex-year-input"
                value={date.year || ""}
                onChange={(e) => setFlexible("year", e.target.value)}
                className="mt-2 w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
                placeholder="2026"
              />
            </div>
          </div>
        ) : null}
      </section>

      <section>
        <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">Where?</h2>
        <p className="mt-2 text-sm text-[#1A1A1A]/65 sm:text-base">
          Tell us the venue(s) — we’ll factor travel & logistics.
        </p>
        <div
          data-testid="location-mode-group"
          role="radiogroup"
          aria-label="Location mode"
          className="mt-5 flex flex-wrap gap-2"
        >
          {LOCATION_MODES.map((m) => {
            const active = locationMode === m.key;
            return (
              <button
                key={m.key}
                role="radio"
                aria-checked={active}
                data-testid={`location-mode-${m.key}`}
                type="button"
                onClick={() => setLocationMode(m.key)}
                className={[
                  "rounded-full border px-4 py-2 text-xs uppercase tracking-[0.16em] transition",
                  active
                    ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF7F2]"
                    : "border-[#1A1A1A]/15 text-[#1A1A1A]/80 hover:border-[#C8A24B] hover:text-[#C8A24B]",
                ].join(" ")}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {locationMode === "destination" ? (
          <div className="mt-5 max-w-md">
            <label htmlFor="dest" className="text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
              Preferred destination
            </label>
            <input
              id="dest"
              type="text"
              data-testid="destination-input"
              value={payload?.destination || ""}
              onChange={(e) => setField("destination", e.target.value)}
              placeholder="e.g. Jaipur, Goa, Bali..."
              className="mt-2 w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
            />
          </div>
        ) : null}

        {(locationMode === "single" || locationMode === "multiple") ? (
          <div className="mt-6 space-y-4" data-testid="location-rows">
            {locations.map((loc, idx) => (
              <div
                key={idx}
                data-testid={`location-row-${idx}`}
                className="rounded-xl border border-[#EAE3D2] bg-white p-4 sm:p-5"
              >
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#C8A24B]">
                    <MapPin className="h-3.5 w-3.5" /> Location {idx + 1}
                  </div>
                  {locations.length > 1 ? (
                    <button
                      type="button"
                      aria-label="Remove location"
                      data-testid={`location-remove-${idx}`}
                      onClick={() => removeLocation(idx)}
                      className="rounded-full p-1.5 text-[#1A1A1A]/45 transition hover:bg-[#EAE3D2]/40 hover:text-[#1A1A1A]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="City">
                    <select
                      data-testid={`location-city-${idx}`}
                      value={loc.city || ""}
                      onChange={(e) => updateLocation(idx, "city", e.target.value)}
                      className="w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
                    >
                      <option value="">— Select —</option>
                      {CITIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Country">
                    <input
                      data-testid={`location-country-${idx}`}
                      value={loc.country || "India"}
                      onChange={(e) => updateLocation(idx, "country", e.target.value)}
                      className="w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
                    />
                  </Field>
                  <Field label="Venue name">
                    <input
                      data-testid={`location-venue-${idx}`}
                      value={loc.venue_name || ""}
                      onChange={(e) => updateLocation(idx, "venue_name", e.target.value)}
                      className="w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
                      placeholder="e.g. ITC Grand Chola"
                    />
                  </Field>
                  <Field label="Indoor / Outdoor">
                    <div role="radiogroup" className="flex flex-wrap gap-2">
                      {["indoor", "outdoor", "both"].map((opt) => {
                        const active = loc.indoor_outdoor === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            data-testid={`location-io-${idx}-${opt}`}
                            onClick={() => updateLocation(idx, "indoor_outdoor", opt)}
                            className={[
                              "rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] transition",
                              active
                                ? "border-[#C8A24B] bg-[#C8A24B] text-[#1A1A1A]"
                                : "border-[#1A1A1A]/15 text-[#1A1A1A]/70 hover:border-[#C8A24B]",
                            ].join(" ")}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </div>
                <Field label="Address (optional)">
                  <textarea
                    data-testid={`location-address-${idx}`}
                    value={loc.address || ""}
                    onChange={(e) => updateLocation(idx, "address", e.target.value)}
                    rows={2}
                    className="w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
                  />
                </Field>
              </div>
            ))}

            {locationMode === "multiple" && locations.length < 5 ? (
              <button
                type="button"
                data-testid="location-add"
                onClick={addLocation}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-[#C8A24B]/60 px-4 py-2 text-xs uppercase tracking-[0.22em] text-[#C8A24B] transition hover:border-[#C8A24B] hover:bg-[#C8A24B]/5"
              >
                <Plus className="h-3.5 w-3.5" /> Add another location
              </button>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55">
        {label}
      </span>
      <span className="mt-2 block">{children}</span>
    </label>
  );
}

function DateStatus({ status }) {
  if (!status) return null;
  const map = {
    standard: { label: "Standard", icon: "✓", cls: "border-[#1A1A1A]/15 text-[#1A1A1A]/70" },
    peak: { label: "Peak date — premium pricing", icon: "⚡", cls: "border-[#C8A24B] text-[#C8A24B]" },
    unavailable: {
      label: "This date is currently unavailable — try a nearby date",
      icon: "✕",
      cls: "border-[#a33] text-[#a33]",
    },
  };
  const s = map[status] || null;
  if (!s) return null;
  return (
    <p
      data-testid={`date-status-${status}`}
      className={`mt-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.18em] ${s.cls}`}
    >
      {status === "unavailable" ? (
        <AlertTriangle className="h-3 w-3" />
      ) : (
        <span aria-hidden>{s.icon}</span>
      )}
      {s.label}
    </p>
  );
}

export function step2IsValid(payload) {
  const mode = payload?.location_mode;
  if (!mode) return false;
  if (mode === "destination") return Boolean(payload?.destination);
  if (mode === "single" || mode === "multiple") {
    return (payload?.locations || []).some((l) => l.city);
  }
  return false;
}
